<script lang="ts">
  import { t } from '$i18n/index';
  import { navigate } from '$ui/router';
  import { app } from '$ui/app-state.svelte';
  import { logCatch, surfacePlanFor } from '$app/catch-service';
  import type { SurfacedPlan } from '$app/catch-service';
  import TagPicker from '$ui/components/TagPicker.svelte';
  import Resolution from '$ui/components/Resolution.svelte';


  /**
   * The core loop, as a three-step flow rather than three routes.
   *
   * Tap 1 opens the tag picker. Tap 2 writes `catch.logged` — the Catch is
   * complete there, and a user who closes the app at that point has done a full
   * rep of skill one. Tap 3 is the Comeback; tap 4 the optional "until when?".
   *
   * Two taps to log, at most four to resolve (spec §3.2). Nothing in this flow
   * may exceed those counts.
   */
  type Step =
    | { name: 'idle' }
    | { name: 'tagging' }
    | { name: 'resolving'; catchId: string; plan: SurfacedPlan | null };

  let step = $state<Step>({ name: 'idle' });
  let justLogged = $state(false);
  let loggedTimer: ReturnType<typeof setTimeout> | undefined;
  let catchButton = $state<HTMLButtonElement | null>(null);

  function beginCatch() {
    justLogged = false;
    step = { name: 'tagging' };
  }

  async function chooseTag(tag: string) {
    // The context is awaited here, in the action, and never by the render —
    // which is what lets the button above be tappable before IndexedDB opens.
    const ctx = await app.context();
    const { catchId } = await logCatch(ctx, tag);
    // Surfacing is recorded on the way in, so the plan is never on screen
    // without the log knowing it was offered (spec §6.3).
    const anchors = new Map(
      app.derived.onboarding.directions.map((d) => [d.directionId, d.label] as const),
    );
    const plan = await surfacePlanFor(ctx, catchId, app.derived.plans, anchors);
    // Show the plan we already recorded as surfaced, whether or not the replay
    // that follows succeeds. Letting a failed read hide it would leave the log
    // counting an offer the user never saw — and I4's revision loop reads those
    // counts as evidence about the plan.
    step = { name: 'resolving', catchId, plan };
    await app.refresh().catch(() => undefined);
  }

  function finish() {
    step = { name: 'idle' };
    // Return focus to the Catch button, so a keyboard or switch user is not
    // dropped onto the document body by a screen that swapped underneath them.
    // Only on the way *back* — focusing it on first load would ring the button
    // before anyone has interacted with anything.
    queueMicrotask(() => catchButton?.focus({ preventScroll: true }));
    justLogged = true;
    clearTimeout(loggedTimer);
    // Long enough to be read, short enough that the app is plainly getting out
    // of the way rather than asking for another moment of attention.
    loggedTimer = setTimeout(() => (justLogged = false), 2400);
  }
</script>

{#if step.name === 'idle'}
  <main class="catch">
    <button type="button" class="catch-button" bind:this={catchButton} onclick={beginCatch}>
      {t('catch.button')}
    </button>

    <!-- The intention line belongs here, beneath the button (spec §6.2). It
         needs derived state — today's morning prediction, the matching plan,
         the current phase — none of which exists yet, and silence beats
         generic filler. I3 fills this space or leaves it empty on purpose. -->

    <p class="motif">{t('app.motif')}</p>

    <p class="logged" aria-live="polite">{justLogged ? t('catch.logged') : ''}</p>
  </main>

  <nav>
    <button type="button" class="quiet" onclick={() => navigate('today')}>
      {t('catch.today')}
    </button>
    <!-- The way back into onboarding for anyone who left it, and the fallback
         for a browser where the first-run flag cannot be stored. An offer
         sitting quietly beside "Today" — never a banner, never a nag. -->
    {#if !app.derived.onboarding.completed}
      <button type="button" class="quiet" onclick={() => navigate('onboard')}>
        {t('catch.setUp')}
      </button>
    {/if}
  </nav>
{:else if step.name === 'tagging'}
  <TagPicker onpick={chooseTag} onback={() => (step = { name: 'idle' })} />
{:else}
  <Resolution catchId={step.catchId} plan={step.plan} ondone={finish} />
{/if}

<style>
  /* Content sits in the lower half rather than dead centre: this is reached
     with a thumb, on a phone already in someone's hand (spec §7 — one hand, one
     screen, no scrolling for core actions). */
  .catch {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-6) var(--space-7);
  }

  /*
   * Deliberately enormous: it is tapped in the half-second after noticing,
   * often without looking properly.
   *
   * An outline rather than a filled disc. A solid button is a call to action —
   * it asks to be pressed, and this one should not ask for anything. Drawn as a
   * ring, it reads as a place to arrive at, which is closer to what the moment
   * actually is.
   */
  .catch-button {
    width: min(70vw, 16.5rem);
    aspect-ratio: 1;
    border-radius: 50%;
    background: transparent;
    border: 1px solid var(--c-accent-line);
    color: var(--c-accent);
    font-size: var(--step-2);
    font-weight: 400;
    letter-spacing: 0.005em;
    transition: transform 140ms var(--ease), border-color 140ms var(--ease);
  }

  .catch-button:active {
    transform: scale(0.98);
    border-color: var(--c-accent);
  }

  .motif {
    color: var(--c-ink-faint);
    font-size: var(--step-0);
    font-style: italic;
    letter-spacing: 0.04em;
  }

  /* Reserves its own line so the layout does not jump when the confirmation
     appears and disappears. */
  .logged {
    min-height: 1.5em;
    color: var(--c-ink-muted);
    font-size: var(--step--1);
    transition: opacity 300ms var(--ease);
  }

  nav {
    display: flex;
    gap: var(--space-4);
    justify-content: center;
    padding: var(--space-3) var(--space-4) var(--space-5);
  }

  .quiet {
    color: var(--c-ink-faint);
    font-size: var(--step--1);
    padding: var(--space-2) var(--space-4);
  }
</style>
